/**
 * Inline "forgot password" form for the login page (PRD-33, ADR-0030;
 * redesigned in PRD-60 chunk A).
 *
 * Posts the email to POST /api/public/password-reset/request via the JWT-free
 * public axios instance (see ../api.ts). The backend ALWAYS returns 200 to
 * avoid account enumeration, so the success copy is the same regardless —
 * it echoes the address that was typed and says to check spam, because that
 * is where a first reset email from a new sender lands. The only distinct
 * error is a 429 (IP rate limit).
 *
 * The email typed on the login form arrives pre-filled (WCAG 3.3.7); the
 * heading takes focus when the form appears and when it succeeds, so the
 * swap is announced instead of dropping focus on <body>.
 */
import { Button } from "@components/ui/Button"
import { Field, TextInput } from "@components/ui/Field"
import { logger } from "@lib/logger"
import { ArrowLeft, CheckCircle2 } from "lucide-react"
import { useEffect, useRef, useState, type FormEvent } from "react"
import { useTranslation } from "react-i18next"

import { setupErrorCode } from "../api"
import { useRequestPasswordReset } from "../hooks"

interface Props {
  /** The address already typed on the login form, if any. */
  initialEmail?: string
  /** Return to the login form. */
  onBack: () => void
}

const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function ForgotPasswordForm({ initialEmail = "", onBack }: Props) {
  const { t } = useTranslation("auth")
  const requestReset = useRequestPasswordReset()
  const [email, setEmail] = useState(initialEmail)
  const [done, setDone] = useState(false)
  const [fieldError, setFieldError] = useState<string | undefined>()
  const [error, setError] = useState("")
  const heading = useRef<HTMLHeadingElement>(null)

  useEffect(() => {
    heading.current?.focus()
  }, [done])

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError("")
    const trimmed = email.trim()
    if (!EMAIL_SHAPE.test(trimmed)) {
      // Without this the backend's enumeration-safe 200 turned an empty or
      // malformed address into a false "we sent you a link".
      setFieldError(t("forgot.error.invalid_email"))
      return
    }
    setFieldError(undefined)
    try {
      await requestReset.mutateAsync(trimmed)
      setDone(true)
    } catch (err) {
      const code = setupErrorCode(err)
      logger.warning("auth.password_reset_request_failed", { code })
      setError(
        t(code === "setup_ip_rate_limited" ? "forgot.error.rate_limited" : "forgot.error.generic"),
      )
    }
  }

  if (done) {
    return (
      <div className="flex flex-col gap-4">
        <div role="status" aria-live="polite" className="flex items-start gap-3">
          <span
            aria-hidden
            className="bg-success-soft text-success-ink flex h-10 w-10 shrink-0 items-center justify-center rounded-full"
          >
            <CheckCircle2 className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <h1
              ref={heading}
              tabIndex={-1}
              className="text-ink text-lg font-semibold focus:outline-none"
            >
              {t("forgot.success.title")}
            </h1>
            <p className="text-ink-muted mt-1 text-sm leading-relaxed">
              {t("forgot.success.body", { email: email.trim() })}
            </p>
          </div>
        </div>
        <Button
          type="button"
          variant="ghost"
          onClick={onBack}
          leftIcon={<ArrowLeft className="h-4 w-4" aria-hidden />}
          className="self-start"
        >
          {t("forgot.back")}
        </Button>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h1
          ref={heading}
          tabIndex={-1}
          className="text-ink text-2xl font-bold tracking-tight focus:outline-none"
        >
          {t("forgot.title")}
        </h1>
        <p className="text-ink-muted mt-2 text-sm">{t("forgot.description")}</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        <Field label={t("forgot.email.label")} error={fieldError}>
          {({ id, describedBy, invalid }) => (
            <TextInput
              id={id}
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("forgot.email.placeholder")}
              aria-describedby={describedBy}
              aria-invalid={invalid}
            />
          )}
        </Field>

        {error && (
          <p
            role="alert"
            className="rounded-control border-danger bg-danger-soft text-danger-ink border px-3 py-2 text-sm"
          >
            {error}
          </p>
        )}

        <Button type="submit" size="lg" className="w-full" loading={requestReset.isPending}>
          {requestReset.isPending ? t("forgot.submitting") : t("forgot.submit")}
        </Button>

        <Button
          type="button"
          variant="ghost"
          onClick={onBack}
          leftIcon={<ArrowLeft className="h-4 w-4" aria-hidden />}
        >
          {t("forgot.back")}
        </Button>
      </form>
    </div>
  )
}
